@file:OptIn(kotlin.concurrent.atomics.ExperimentalAtomicApi::class)

package com.dotpx.ambient

import kotlin.concurrent.atomics.AtomicBoolean

internal val SelectableEventKinds: List<EventKind> =
    EventKind.entries.filter { it != EventKind.None }

/**
 * Session-scoped shuffle bags for audible materials. Texture and event kinds
 * are drawn without replacement until the bag is exhausted, then refilled
 * excluding the last drawn item so a cycle boundary cannot repeat.
 */
internal class SoundMaterialCycle {
    private val locked = AtomicBoolean(false)
    private val remainingTextures = mutableSetOf<TextureKind>()
    private val remainingEvents = mutableSetOf<EventKind>()
    private var lastTexture: TextureKind? = null
    private var lastEvent: EventKind? = null

    init {
        resetUnlocked()
    }

    fun reset() = withLock { resetUnlocked() }

    fun <T> modify(block: Mutator.() -> T): T = withLock { Mutator().block() }

    fun availableTextures(): List<TextureKind> = modify { availableTextures() }

    fun availableEvents(excluding: EventKind = EventKind.None): List<EventKind> =
        modify { availableEvents(excluding) }

    fun consume(texture: TextureKind, event: EventKind, secondary: EventKind) {
        modify { consume(texture, event, secondary) }
    }

    inner class Mutator {
        fun availableTextures(): List<TextureKind> {
            if (remainingTextures.isEmpty()) refillTextures(exclude = lastTexture)
            return TextureKind.entries.filter { it in remainingTextures }
        }

        fun availableEvents(excluding: EventKind = EventKind.None): List<EventKind> {
            if (remainingEvents.isEmpty()) refillEvents(exclude = lastEvent)
            return SelectableEventKinds.filter { it in remainingEvents && it != excluding }
        }

        fun canTakeExact(
            texture: TextureKind,
            event: EventKind,
            secondary: EventKind,
        ): Boolean {
            if (event != EventKind.None && event == secondary) return false
            if (texture !in remainingTextures) return false
            return listOf(event, secondary)
                .filter { it != EventKind.None }
                .all { it in remainingEvents }
        }

        fun consume(texture: TextureKind, event: EventKind, secondary: EventKind) {
            remainingTextures.remove(texture)
            lastTexture = texture
            if (remainingTextures.isEmpty()) refillTextures(exclude = texture)
            consumeEvent(event)
            consumeEvent(secondary)
        }

        private fun consumeEvent(kind: EventKind) {
            if (kind == EventKind.None) return
            remainingEvents.remove(kind)
            lastEvent = kind
            if (remainingEvents.isEmpty()) refillEvents(exclude = kind)
        }
    }

    private fun resetUnlocked() {
        lastTexture = null
        lastEvent = null
        refillTextures(exclude = null)
        refillEvents(exclude = null)
    }

    private fun refillTextures(exclude: TextureKind?) {
        remainingTextures.clear()
        remainingTextures.addAll(TextureKind.entries)
        if (exclude != null && remainingTextures.size > 1) remainingTextures.remove(exclude)
    }

    private fun refillEvents(exclude: EventKind?) {
        remainingEvents.clear()
        remainingEvents.addAll(SelectableEventKinds)
        if (exclude != null && exclude != EventKind.None && remainingEvents.size > 1) {
            remainingEvents.remove(exclude)
        }
    }

    private fun <T> withLock(block: () -> T): T {
        while (!locked.compareAndSet(expectedValue = false, newValue = true)) {
            // Exclusive UI/audio access for a handful of enum draws.
        }
        try {
            return block()
        } finally {
            locked.store(false)
        }
    }
}
